/**
 * ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6, Jay's answers 10-30): the Discharge summary note's own
 * rules, shared so the editor, the sign-off check and the API read one list.
 *
 *  - Final goal statuses: Met, Partially met, Not met and Discontinued. In progress, Not started and
 *    Not addressed this session block sign-off; Discontinued needs a reason (the row's note).
 *    The rule follows the practice's "Goal status required at sign-off" (on unless turned off).
 *  - No treatment plan on file: the note signs, with a reason from the practice's list (Assessment
 *    only, Left before plan completed, Single session/crisis, Transferred out; admins and
 *    supervisors add to it in Note settings).
 *  - Once it is final (signed, or co-signed for an associate) the EHR sets the client's stage to
 *    Discharged, closes the client's current treatment plan and cancels their future appointments
 *    (once the Calendar has its route for it; until then they are listed for staff to cancel).
 *    DischargePreview is what the editor's confirm lists first; DischargeOutcome is what happened.
 */
/** The note kind these rules are for. */
export const DISCHARGE_KIND = 'Discharge summary';
export const isDischargeKind = (kind) => kind === DISCHARGE_KIND;
/** Jay 10-30 Q1: the statuses that count as final on a discharge summary. */
export const DISCHARGE_FINAL_STATUSES = [
    'Met',
    'Partially met',
    'Not met',
    'Discontinued',
];
/** Q1: Discontinued needs a reason (kept in the row's status note). */
export const REASON_STATUSES = ['Discontinued'];
/** Q2: the "No treatment plan on file" reasons a practice starts with (it can add to them). */
export const DISCHARGE_NO_PLAN_REASONS_DEFAULT = [
    'Assessment only',
    'Left before plan completed',
    'Single session/crisis',
    'Transferred out',
];
/** The reason list's limits: how many, and how long each one may be. */
export const NO_PLAN_REASONS_MAX = 30;
export const NO_PLAN_REASON_MAX = 80;
/** What the sign-off list calls a missing reason, and where it jumps to. */
export const NO_PLAN_REASON_LABEL = 'No treatment plan on file reason';
/** A reason as it is kept: one line, no extra spaces, at most NO_PLAN_REASON_MAX long. */
export const cleanNoPlanReason = (t) => t.replace(/\s+/g, ' ').trim().slice(0, NO_PLAN_REASON_MAX);
/**
 * The practice's reason list from what is stored: each one cleaned, no repeats (any case), at most
 * NO_PLAN_REASONS_MAX. Nothing stored (or nothing usable) reads as the default list.
 */
export function noPlanReasonsOf(v) {
    if (!Array.isArray(v))
        return [...DISCHARGE_NO_PLAN_REASONS_DEFAULT];
    const out = [];
    for (const x of v) {
        if (typeof x !== 'string')
            continue;
        const t = cleanNoPlanReason(x);
        if (t && !out.some((o) => o.toLowerCase() === t.toLowerCase()))
            out.push(t);
        if (out.length >= NO_PLAN_REASONS_MAX)
            break;
    }
    return out.length ? out : [...DISCHARGE_NO_PLAN_REASONS_DEFAULT];
}
/**
 * Q2: a discharge summary with no plan to give statuses to (no linked plan and no goal rows) needs
 * its "No treatment plan on file" reason. Any other kind, or a note with a plan, asks nothing.
 */
export function noPlanReasonLeft(o) {
    if (!isDischargeKind(o.kind))
        return [];
    const linked = typeof o.linkedPlanId === 'string' && o.linkedPlanId !== '';
    if (linked || o.goalRows > 0)
        return [];
    if (typeof o.reason === 'string' && o.reason.trim())
        return [];
    return [{ label: NO_PLAN_REASON_LABEL, target: 'goals', noPlan: true }];
}
/** Everything happened: nothing is left for staff to finish elsewhere (else the card asks them). */
export const dischargeOutcomeDone = (o) => o.stage !== 'missing' && o.plan !== 'failed' && o.notCancelled === 0 && !o.leftBooked;
/** The plain line the editor shows once a discharge summary is final. */
export function dischargeOutcomeText(o) {
    const parts = [];
    if (o.stage === 'set')
        parts.push('Client set to Discharged');
    else if (o.stage === 'already')
        parts.push('Client was already Discharged');
    else
        parts.push('No Discharged stage in Settings, so the client stage was not changed');
    if (o.plan === 'closed')
        parts.push('treatment plan closed');
    else if (o.plan === 'failed')
        parts.push('the treatment plan could not be closed (close it in the Treatment Plan app)');
    if (o.cancelled)
        parts.push(`${o.cancelled} future appointment${o.cancelled === 1 ? '' : 's'} cancelled`);
    if (o.notCancelled)
        parts.push(`${o.notCancelled} appointment${o.notCancelled === 1 ? '' : 's'} could not be cancelled (cancel ${o.notCancelled === 1 ? 'it' : 'them'} in the Calendar)`);
    if (o.leftBooked)
        parts.push(`${o.leftBooked} future appointment${o.leftBooked === 1 ? ' is' : 's are'} still on the Calendar (cancel ${o.leftBooked === 1 ? 'it' : 'them'} there)`);
    const s = parts.join(', ');
    return `${s.charAt(0).toUpperCase()}${s.slice(1)}.`;
}
// ── Who co-signs a discharge summary (Q5) ────────────────────────────────────────────────────
/**
 * Q5: a supervised provider's discharge summary goes to a supervisor whose row on them ticks
 * "Co-sign discharge summaries": their primary supervisor when theirs does, else the first ticked
 * (oldest row). With none ticked it still goes to the primary supervisor, as every note does today:
 * the switch starts off on every row (and a supervisor picked only on the profile has no row), so
 * reading "none ticked" as "signs alone" would send every associate's discharge summary, which
 * discharges the client at once, past review the day it shipped. Whether an unticked switch should
 * let the associate sign alone is Jay's call (an open question on ehr#708). A provider whose
 * Supervision is off signs alone, as on every note.
 */
export function dischargeCosigner(o) {
    if (!o.required)
        return { required: false, supervisorId: null };
    const ok = o.ticked.filter((id) => id && id !== o.authorId);
    if (o.primaryId && ok.includes(o.primaryId))
        return { required: true, supervisorId: o.primaryId };
    return { required: true, supervisorId: ok[0] ?? o.primaryId };
}
//# sourceMappingURL=dischargeNote.js.map